import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ApiError,
  PlanCatalogEntry,
  listPlans,
  signupTenant
} from "../lib/api.js";

type PlanStatus =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; plans: PlanCatalogEntry[] };

function formatPrice(plan: PlanCatalogEntry): string {
  if (plan.priceMonthlyUsd === 0) return "Free";
  return `$${plan.priceMonthlyUsd.toLocaleString()}/mo`;
}

function suggestSlug(organizationName: string): string {
  return organizationName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestedPlanId = params.get("plan");

  const [planState, setPlanState] = useState<PlanStatus>({ status: "loading" });
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [organizationName, setOrganizationName] = useState("");
  const [workspaceSlug, setWorkspaceSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerUsername, setOwnerUsername] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const plans = await listPlans();
        if (cancelled) return;
        setPlanState({ status: "ready", plans });
        const preselect =
          (requestedPlanId && plans.find((p) => p.id === requestedPlanId)) ||
          plans.find((p) => p.id === "starter") ||
          plans[0];
        if (preselect) setSelectedPlanId(preselect.id);
      } catch (err) {
        if (cancelled) return;
        setPlanState({
          status: "error",
          message: err instanceof Error ? err.message : "Unable to load plans"
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestedPlanId]);

  useEffect(() => {
    if (!slugTouched) setWorkspaceSlug(suggestSlug(organizationName));
  }, [organizationName, slugTouched]);

  const plans = planState.status === "ready" ? planState.plans : [];
  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === selectedPlanId) ?? null,
    [plans, selectedPlanId]
  );

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPlanId) {
      setSignupError("Select a subscription plan to continue.");
      return;
    }
    if (ownerPassword !== confirmPassword) {
      setSignupError("Password confirmation does not match.");
      return;
    }
    setSignupError(null);
    setSubmitting(true);
    try {
      await signupTenant({
        organizationName: organizationName.trim(),
        workspaceSlug: workspaceSlug.trim().toLowerCase(),
        planId: selectedPlanId,
        ownerName: ownerName.trim(),
        ownerEmail: ownerEmail.trim(),
        ownerUsername: ownerUsername.trim(),
        ownerPassword
      });
      navigate("/app", { replace: true });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.message}${err.code ? ` (${err.code})` : ""}`
          : err instanceof Error
            ? err.message
            : "Signup failed. Please try again.";
      setSignupError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-bar">
        <Link to="/" className="brand">
          <img src="/brand-logo-light.svg" alt="TallyStream" />
        </Link>
        <Link to="/login" className="btn">I already have an account</Link>
      </div>

      <div className="auth-body" style={{ alignItems: "flex-start" }}>
        <div style={{ width: "100%", maxWidth: 980, margin: "0 auto", padding: "24px 16px" }}>
          <h1 style={{ marginTop: 0 }}>Create your TallyStream workspace</h1>
          <p className="muted" style={{ maxWidth: 640 }}>
            Self-service signup. You become the workspace owner and can invite members up to
            the seat limit of your selected plan. Cancel anytime.
          </p>

          <section aria-labelledby="plan-heading" style={{ margin: "24px 0" }}>
            <h2 id="plan-heading" style={{ fontSize: 18, marginBottom: 8 }}>1. Choose a plan</h2>
            {planState.status === "loading" && <p className="muted">Loading plans…</p>}
            {planState.status === "error" && (
              <div className="alert alert-error">{planState.message}</div>
            )}
            {planState.status === "ready" && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12
                }}
              >
                {planState.plans.map((plan) => {
                  const isSelected = plan.id === selectedPlanId;
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => setSelectedPlanId(plan.id)}
                      className="card"
                      style={{
                        padding: 16,
                        textAlign: "left",
                        cursor: "pointer",
                        borderWidth: 2,
                        borderStyle: "solid",
                        borderColor: isSelected ? "var(--accent, #2563eb)" : "transparent",
                        background: "#fff",
                        position: "relative"
                      }}
                    >
                      {plan.recommended && (
                        <span
                          className="pill pill-info"
                          style={{
                            position: "absolute",
                            top: -8,
                            right: 12,
                            background: "var(--accent, #2563eb)",
                            color: "#fff",
                            fontSize: 11
                          }}
                        >
                          Recommended
                        </span>
                      )}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <strong style={{ fontSize: 16 }}>{plan.name}</strong>
                        <span className="pill" style={{ textTransform: "capitalize" }}>{plan.tier}</span>
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, margin: "6px 0" }}>{formatPrice(plan)}</div>
                      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                        {plan.limits.seats} seats · {plan.limits.dhis2Instances} DHIS2 · {plan.limits.datasets} datasets
                      </div>
                      <ul style={{ paddingLeft: 16, margin: 0, fontSize: 12 }}>
                        {plan.features.slice(0, 4).map((feature) => (
                          <li key={feature} style={{ marginBottom: 2 }}>{feature}</li>
                        ))}
                      </ul>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <form onSubmit={onSubmit} className="card" style={{ padding: 20 }}>
            <h2 style={{ fontSize: 18, marginTop: 0 }}>2. Organisation &amp; owner</h2>

            {selectedPlan && (
              <div className="alert" style={{ marginBottom: 16 }}>
                Plan: <strong>{selectedPlan.name}</strong> · {formatPrice(selectedPlan)} ·{" "}
                {selectedPlan.limits.seats} seats · {selectedPlan.limits.dhis2Instances} DHIS2 instance{selectedPlan.limits.dhis2Instances === 1 ? "" : "s"} ·{" "}
                {selectedPlan.limits.datasets} datasets · {selectedPlan.limits.publicPortals} portal{selectedPlan.limits.publicPortals === 1 ? "" : "s"}
              </div>
            )}

            <div className="field">
              <label htmlFor="organizationName">Organisation name</label>
              <input
                id="organizationName"
                type="text"
                required
                minLength={2}
                maxLength={120}
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Acme Health"
                autoFocus
              />
            </div>

            <div className="field">
              <label htmlFor="workspaceSlug">Workspace slug</label>
              <input
                id="workspaceSlug"
                type="text"
                required
                minLength={3}
                maxLength={40}
                pattern="^[a-z0-9][a-z0-9-]*[a-z0-9]$"
                value={workspaceSlug}
                onChange={(e) => { setSlugTouched(true); setWorkspaceSlug(e.target.value); }}
                placeholder="acme-health"
              />
              <small className="muted">Lowercase letters, digits, and hyphens.</small>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div className="field">
                <label htmlFor="ownerName">Your name</label>
                <input
                  id="ownerName"
                  type="text"
                  required
                  minLength={2}
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  autoComplete="name"
                />
              </div>
              <div className="field">
                <label htmlFor="ownerEmail">Work email</label>
                <input
                  id="ownerEmail"
                  type="email"
                  required
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div className="field">
                <label htmlFor="ownerUsername">Username</label>
                <input
                  id="ownerUsername"
                  type="text"
                  required
                  minLength={3}
                  maxLength={60}
                  pattern="^[A-Za-z0-9._-]+$"
                  value={ownerUsername}
                  onChange={(e) => setOwnerUsername(e.target.value)}
                  autoComplete="username"
                />
              </div>
              <div className="field">
                <label htmlFor="ownerPassword">Password</label>
                <input
                  id="ownerPassword"
                  type="password"
                  required
                  minLength={8}
                  maxLength={128}
                  value={ownerPassword}
                  onChange={(e) => setOwnerPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <small className="muted">At least 8 characters.</small>
              </div>
            </div>

            <div className="field">
              <label htmlFor="confirmPassword">Confirm password</label>
              <input
                id="confirmPassword"
                type="password"
                required
                minLength={8}
                maxLength={128}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>

            {signupError && (
              <div className="alert alert-error" style={{ marginTop: 12 }}>{signupError}</div>
            )}

            <div style={{ marginTop: 16, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || !selectedPlanId}
              >
                {submitting ? "Creating workspace…" : "Create workspace"}
              </button>
              <Link to="/login" className="btn">I already have an account</Link>
            </div>
          </form>
        </div>
      </div>

      <div className="auth-foot">
        <span>TallyStream · VitalPlatforms</span>
        <span>tallystream.vitalplatforms.com</span>
      </div>
    </div>
  );
}
